import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import UserProfile from './UserProfile';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSystemSettings, setShowSystemSettings] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  const getUserTypeTitle = () => {
    switch (user?.userType) {
      case 'rider': return 'Customer';
      case 'driver': return 'Driver';
      case 'admin': return 'Administrator';
      default: return 'User';
    }
  };

  const getUserTypeIcon = () => {
    switch (user?.userType) {
      case 'rider': return '👤';
      case 'driver': return '🚗';
      case 'admin': return '🛠️';
      default: return '👤';
    }
  };

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_pwa-vercel-prod/artifacts/z056vcil_Logo.png.png" 
            alt="Companion Cab" 
            className="logo-icon"
            onError={(e) => {
              // Fallback to heart emoji if image fails
              e.target.style.display = 'none';
              const heartIcon = document.createElement('div');
              heartIcon.innerHTML = '💖';
              heartIcon.className = 'logo-heart-fallback';
              heartIcon.style.fontSize = '2rem';
              e.target.parentNode.insertBefore(heartIcon, e.target);
            }}
          />
          <div className="modern-logo">
            <span className="logo-text">Companion</span>
            <span className="logo-accent">Cab</span>
          </div>
        </div>

        {isAuthenticated && (
          <nav className="header-nav">
            <div className="user-section">
              <div className="user-info">
                <span className="user-type-icon">{getUserTypeIcon()}</span>
                <div className="user-details">
                  <span className="user-name">{user?.name}</span>
                  <span className="user-type">{getUserTypeTitle()}</span>
                </div>
              </div>

              <div className="user-menu" ref={dropdownRef}>
                <button 
                  className="user-menu-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <div className="user-initial-avatar">
                    {(() => {
                      const name = user?.name || user?.username || user?.email || '';
                      console.log('User data for avatar:', { user, name }); // Debug log
                      return name.charAt(0)?.toUpperCase() || '?';
                    })()}
                  </div>
                </button>

                {showUserMenu && (
                  <div className="user-dropdown">
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowProfile(true);
                        setShowUserMenu(false);
                      }}
                    >
                      <span>👤</span>
                      Profile Settings
                    </button>
                    {user?.userType === 'driver' && (
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('openEarningsReport'));
                          setShowUserMenu(false);
                        }}
                      >
                        <span>📊</span>
                        Earnings Report
                      </button>
                    )}
                    
                    {user?.userType === 'rider' && (
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          // Dispatch event to switch to trips tab
                          window.dispatchEvent(new CustomEvent('headerNavigation', { detail: 'trip-history' }));
                          setShowUserMenu(false);
                        }}
                      >
                        <span>📄</span>
                        Trip History
                      </button>
                    )}
                    
                    {user?.userType === 'admin' && (
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          setShowSystemSettings(true);
                          setShowUserMenu(false);
                        }}
                      >
                        <span>⚙️</span>
                        System Settings
                      </button>
                    )}

                    <hr className="dropdown-divider" />
                    
                    <button 
                      className="dropdown-item logout"
                      onClick={handleLogout}
                    >
                      <span>🚪</span>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>

      {/* User Profile Modal */}
      {showProfile && (
        <UserProfile 
          onClose={() => setShowProfile(false)}
        />
      )}

      {/* System Settings Modal */}
      {showSystemSettings && (
        <div className="login-overlay" onClick={() => setShowSystemSettings(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="login-header">
              <h2>System Settings</h2>
              <button className="close-btn" onClick={() => setShowSystemSettings(false)}>✕</button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>Application Settings</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ fontWeight: '500', color: '#64748b' }}>App Version</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>v2.0.0</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ fontWeight: '500', color: '#64748b' }}>Database Status</span>
                    <span style={{ fontWeight: '600', color: '#10b981' }}>Connected</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ fontWeight: '500', color: '#64748b' }}>Total Users</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>9</span>
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>System Actions</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <button 
                    className="btn-secondary"
                    style={{ width: '100%', padding: '1rem', borderRadius: '12px' }}
                    onClick={() => alert('User management features coming soon!')}
                  >
                    📊 View System Analytics
                  </button>
                  <button 
                    className="btn-secondary"
                    style={{ width: '100%', padding: '1rem', borderRadius: '12px' }}
                    onClick={() => alert('Backup features coming soon!')}
                  >
                    💾 Backup Database
                  </button>
                  <button 
                    className="btn-secondary"
                    style={{ width: '100%', padding: '1rem', borderRadius: '12px' }}
                    onClick={() => alert('Log viewing features coming soon!')}
                  >
                    📋 View System Logs
                  </button>
                </div>
              </div>
              
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                <p>CompanionCab Admin System</p>
                <p>For technical support, contact: infocompcab@gmail.com</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;